import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown, faAngleUp, faCircleQuestion } from "@fortawesome/free-solid-svg-icons";
import "./earning.css";
import { getMonthlyEarnings } from "../../../../shared/actions/transactionAction";
import { Pagination } from "../../../../shared/utils/paginationUtil";
import { formatUsdAmountDisplayCents } from "../../../../shared/utils/function";
import {
  BREAKDOWN_LABELS,
  BREAKDOWN_COLORS,
  BREAKDOWN_FIELD_MAP,
  MONTHS
} from '../../../../shared/constants/EarningsPayout';
import VatInfoModal from '../../../Modals/EarningsPayout/VatInfoModal';
import { ROLE } from "../../../../shared/constants/common";
import { reportError } from "../../../../shared/sentry";

const TILES_PER_PAGE = 5;

const MonthlyEarningsCard = ({
  user,
  isVatApplicable,
  isGstApplicable
}) => {
  const [earningsByMonth, setEarningsByMonth] = useState([]);
  const [isShow, setIsShow] = useState(true);
  const [startIndex, setStartIndex] = useState(0);
  const [isVatInfoModalOpen, setIsVatInfoModalOpen] = useState(false);

  const formatMonth = (month) => {
    return MONTHS[month - 1];
  };

  const getBreakdownColor = (label) => {
    return BREAKDOWN_COLORS[label] || null;
  };

  const transformMonthlyEarnings = (apiData) => {
    if (!apiData || !Array.isArray(apiData)) {
      return [];
    }

    return apiData.map((item) => {
      const breakdown = [];

      Object.entries(BREAKDOWN_FIELD_MAP).forEach(([field, label]) => {
        // if amount is not undefined and greater than 0 and 
        // if the label is not Subscriptions or Content Purchases and 
        // if the user role is Creator Sub or Creator Content
        if (item[field] !== undefined && item[field] > 0 &&
          ((label !== BREAKDOWN_LABELS.SUBSCRIPTIONS && label !== BREAKDOWN_LABELS.CONTENT_PURCHASES)
            || (user?.rol === ROLE.CREATOR_SUB && label === BREAKDOWN_LABELS.SUBSCRIPTIONS)
            || (user?.rol === ROLE.CREATOR_CONTENT && label === BREAKDOWN_LABELS.CONTENT_PURCHASES))
        ) {
          breakdown.push({
            label,
            amount: item[field] || 0,
            color: getBreakdownColor(label),
            refId: item.refId || item.ref_id || null
          });
        }
      });

      return {
        month: formatMonth(item.month),
        year: item.year,
        monthString: item.month,
        total: item.netEarnings || 0,
        transactionCount: item.transactionCount || 0,
        isOpen: false,
        breakdown
      };
    });
  };

  useEffect(() => {
    if (user) {
      const fetchMonthlyEarnings = async () => {
        // year: new Date().getFullYear(), month: new Date().getMonth() + 1
        const result = await getMonthlyEarnings({ limit: 10, skip: 0 });
        if (result.success) {
          const transformedData = transformMonthlyEarnings(result.data);
          setEarningsByMonth(transformedData);
          setStartIndex(0);
        } else {
          reportError(result.error, 'monthly earnings fetch');
        }
      };

      fetchMonthlyEarnings();
    }
  }, [user]);

  const handleChildTabs = (absoluteIndex) => {
    const updatedEarnings = [...earningsByMonth];
    updatedEarnings[absoluteIndex].isOpen = !updatedEarnings[absoluteIndex].isOpen;
    setEarningsByMonth(updatedEarnings);
  };

  const visibleEarnings = earningsByMonth.slice(startIndex, startIndex + TILES_PER_PAGE);
  const totalPages = Math.ceil(earningsByMonth.length / TILES_PER_PAGE) || 1;
  const currentPage = Math.floor(startIndex / TILES_PER_PAGE) + 1;

  const handlePageChange = (page) => {
    setStartIndex((page - 1) * TILES_PER_PAGE);
  };

  const organizeBreakdown = (breakdown) => {
    const regular = [];
    let chargebacks = null;
    let vatRemitted = null;
    let netEarnings = null;

    breakdown.forEach(item => {
      if (item.label === BREAKDOWN_LABELS.VAT_REMITTED) {
        vatRemitted = item;
      } else if (item.label === BREAKDOWN_LABELS.NET_EARNINGS) {
        netEarnings = item;
      } else if (item.label === BREAKDOWN_LABELS.CHARGEBACKS) {
        chargebacks = item;
      } else {
        regular.push(item);
      }
    });

    return { regular, chargebacks, vatRemitted, netEarnings };
  };

  return (
    <div className={`g_card ${isShow ? '' : 'card_close'}`}>
      <div className="d-flex justify-content-between align-items-center">
        <h3 className="mb-0">
          <i className='fa-solid fa-chart-line'></i> Monthly Earnings
        </h3>

        {earningsByMonth.length > 0 && (
          <span onClick={() => setIsShow(!isShow)} className="cm_toggle_card">
            <FontAwesomeIcon icon={isShow ? faAngleDown : faAngleUp} />
          </span>
        )}
      </div>

      {isShow && earningsByMonth.length > 0 && (
        <>
          <div className='cm_hr_full'></div>
          
          <ul className="payouts_list">
            {visibleEarnings.map((item, index) => {
              const absoluteIndex = startIndex + index;
              const { regular, chargebacks, vatRemitted, netEarnings } = organizeBreakdown(item.breakdown);
              return (
                <li key={`${item.month}-${absoluteIndex}`} className={`earning-list-item ${item.isOpen ? '' : 'menu_close'}`}>
                  <div className="d-flex justify-content-between align-items-center earning-header-container">
                    <div className="d-flex flex-column earning-content-wrapper">
                      <div className="d-flex align-items-center gap-2 earning-date-row">
                        <p className="earning-date-text">
                          <span className="text-white font_14">
                            {item.month} {item.year}
                          </span>
                        </p>
                        <div className="cm_h_divder"></div>
                        <p className="earning-total-amount">
                          <b>${formatUsdAmountDisplayCents(item.total)}</b>
                        </p>
                      </div>

                    </div>
                    <div>
                      <span
                        onClick={() => handleChildTabs(absoluteIndex)}
                        className="cm_toggle_card_s earning-toggle-button"
                      >
                        <FontAwesomeIcon icon={item.isOpen ? faAngleUp : faAngleDown} />
                      </span>
                    </div>
                  </div>
                  {item.isOpen && (
                    <>
                      {item.breakdown.length > 0 ? (
                        <>
                          <ul className="cm_sm_list_container earning-breakdown-container">
                            {/* Regular breakdown items */}
                            {regular.map((breakdownItem, breakdownIndex) => (
                              <li
                                className="cm_sm_list earning-breakdown-item"
                                key={`${item.month}-breakdown-${breakdownIndex}`}
                              >
                                <div className="d-flex gap-3 align-items-center">
                                  {breakdownItem.color && (
                                    <span
                                      className="cm_label_color"
                                      style={{ background: breakdownItem.color }}
                                    ></span>
                                  )}
                                  <p className="earning-breakdown-label text-white font_14">
                                    {breakdownItem.label}
                                  </p>
                                </div>
                                <div>
                                  <h4 className="earning-breakdown-amount">
                                    ${formatUsdAmountDisplayCents(breakdownItem.amount)}
                                  </h4>
                                </div>
                              </li>
                            ))}

                            {/* Chargebacks */}
                            {chargebacks && chargebacks.amount > 0 && (
                              <li
                                className="cm_sm_list earning-chargebacks-item"
                                key={`${item.month}-chargebacks`}
                              >
                                <div className="d-flex flex-column earning-chargebacks-container">
                                  <p className="earning-chargebacks-label font_14">
                                    {chargebacks.label}
                                  </p>
                                </div>
                                <div>
                                  <p className="earning-chargebacks-amount">
                                    ${formatUsdAmountDisplayCents(chargebacks.amount)}
                                  </p>
                                </div>
                              </li>
                            )}

                            {/* VAT Remitted */}
                            {isVatApplicable && vatRemitted && vatRemitted.amount > 0 && (
                              <li
                                className={`cm_sm_list earning-vat-item ${netEarnings ? 'earning-vat-with-net' : ''}`}
                                key={`${item.month}-vat`}
                              >
                                <div className="d-flex flex-column earning-vat-container">
                                  <p className="earning-vat-label font_14">
                                    {vatRemitted.label}
                                  </p>
                                  <div className="d-flex align-items-center gap-2">
                                    <p className="earning-vat-ref">
                                      {vatRemitted.refId || 'N/A'}
                                    </p>
                                    <FontAwesomeIcon
                                      icon={faCircleQuestion}
                                      className="earning-vat-icon"
                                      onClick={() => setIsVatInfoModalOpen(true)}
                                      style={{ cursor: 'pointer' }}
                                    />
                                  </div>
                                </div>
                                <div>
                                  <h4 className="earning-vat-amount font_12 text-yellow">
                                    ${formatUsdAmountDisplayCents(vatRemitted.amount)}
                                  </h4>
                                </div>
                              </li>
                            )}

                            {/* GST Remitted */}
                            {isGstApplicable && (
                              <li
                                className={`cm_sm_list earning-gst-item ${netEarnings ? 'earning-gst-with-net' : ''}`}
                                key={`${item.month}-gst`}
                              >
                                <div className="d-flex flex-column earning-gst-container">
                                  <p className="earning-gst-label font_14">
                                    GST Number
                                  </p>
                                  <div className="d-flex align-items-center gap-2">
                                    <p className="earning-gst-ref">
                                      {user?.gst_number || 'N/A'}
                                    </p>
                                    <FontAwesomeIcon
                                      icon={faCircleQuestion}
                                      className="earning-vat-icon"
                                      onClick={() => setIsVatInfoModalOpen(true)}
                                      style={{ cursor: 'pointer' }}
                                    />
                                  </div>
                                </div>
                                <div>
                                  
                                </div>
                              </li>
                            )}

                            {/* Net Earnings */}
                            {netEarnings && (
                              <li
                                className="cm_sm_list earning-net-item"
                                key={`${item.month}-net`}
                              >
                                <div>
                                  <p className="earning-net-label font_14 text-white">
                                    {netEarnings.label}
                                  </p>
                                </div>
                                <div>
                                  <h4 className="earning-net-amount text-green text-white">
                                    ${formatUsdAmountDisplayCents(netEarnings.amount)}
                                  </h4>
                                </div>
                              </li>
                            )}
                          </ul>
                        </>
                      ) : (
                        <li className="cm_sm_list">
                          <p>No breakdown available</p>
                        </li>
                      )}
                    </>
                  )}
                </li>
              );
            })}
          </ul>

          {earningsByMonth.length > TILES_PER_PAGE && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              onPreviousPage={() => handlePageChange(currentPage - 1)}
              onNextPage={() => handlePageChange(currentPage + 1)}
            />
          )}
        </>
      )}

      <VatInfoModal
        isOpen={isVatInfoModalOpen}
        toggle={() => setIsVatInfoModalOpen(!isVatInfoModalOpen)}
      />
    </div>
  );
};

export default MonthlyEarningsCard;